import { MutableRefObject, useEffect, useRef, useState } from 'react';

import { BaseSelection as SlateBaseSelection } from 'slate';
import { useSlate } from 'slate-react';

import HorizontalToolbar from 'components/Editor/components/HorizontalToolbar';
import MentionToolbar from 'components/Editor/components/MentionToolbar';
import VerticalToolbar from 'components/Editor/components/VerticalToolbar';
import {
  HORIZONTAL_TOOLBAR_HEIGHT,
  HORIZONTAL_TOOLBAR_WIDTH,
  MENTION_TOOLBAR_HEIGHT,
  MENTION_TOOLBAR_WIDTH,
  VERTICAL_TOOLBAR_HEIGHT,
  VERTICAL_TOOLBAR_WIDTH,
} from 'constants/toolbarList';
import { useOutsideAlerter } from 'hooks/useOutsideAlerter';
import { useOutsideToolbar } from 'hooks/useOutsideToolbar';
import { useRolePermission } from 'hooks/useRolePermission';
import useToolbar from 'hooks/useToolbar';
import { USING_TOOLBAR } from 'types/Editor';
import {
  calculateLeftPosition,
  calculateMentionToolbarTop,
  calculateTopPosition,
  closeToolbar,
  getRect,
  openToolbar,
} from 'utils/editorToolbarHelpers';
import { isSafariBrowser } from 'utils/getBrowserName';

const Toolbar = () => {
  const { permissionUsingTableField, permissionUsingAttachments } = useRolePermission();
  const editor = useSlate();
  const usingToolbar = useToolbar();
  const [horizontalToolbar, setHorizontalToolbar] = useState<boolean>(false);
  const [verticalToolbar, setVerticalToolbar] = useState<boolean>(false);
  const [mentionToolbar, setMentionToolbar] = useState<boolean>(false);
  const [lastEditorSelection, setLastEditorSelection] = useState<SlateBaseSelection | null>(null);

  const horizontalToolbarRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const verticalToolbarRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const verticalToolbarInputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);

  const mentionToolbarRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const mentionToolbarInputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);

  useOutsideAlerter(horizontalToolbarRef, setHorizontalToolbar);

  const outsideVerticalToolbar = useOutsideToolbar(
    verticalToolbarRef,
    verticalToolbarInputRef,
    true,
  );

  const outsideMentionToolbar = useOutsideToolbar(
    mentionToolbarRef,
    mentionToolbarInputRef,
    true,
  );

  const hasFocusVerticalToolbarInput = verticalToolbarInputRef.current?.hasAttribute('data-focus');
  const hasFocusMentionToolbarInput = mentionToolbarInputRef.current?.hasAttribute('data-focus');

  const editorSelection = editor.selection;

  useEffect(() => {
    setVerticalToolbar(!outsideVerticalToolbar);
    setMentionToolbar(!outsideMentionToolbar);
  }, [outsideVerticalToolbar, outsideMentionToolbar]);

  useEffect(() => {
    if (usingToolbar === USING_TOOLBAR.HORIZONTAL_TOOLBAR) return setHorizontalToolbar(true);
    if (usingToolbar === USING_TOOLBAR.VERTICAL_TOOLBAR) return setVerticalToolbar(true);
    if (usingToolbar === USING_TOOLBAR.MENTION_TOOLBAR) return setMentionToolbar(true);

    if (usingToolbar !== USING_TOOLBAR.HORIZONTAL_TOOLBAR) {
      setHorizontalToolbar(false);
    }

    if (!usingToolbar && !hasFocusVerticalToolbarInput) {
      setVerticalToolbar(false);
    }

    if (!usingToolbar && !hasFocusMentionToolbarInput) {
      setMentionToolbar(false);
    }
  }, [usingToolbar, hasFocusVerticalToolbarInput, hasFocusMentionToolbarInput]);

  useEffect(() => {
    if (!usingToolbar) return;

    const rect = getRect();

    if (
      !horizontalToolbarRef.current
      || !verticalToolbarRef.current
      || !mentionToolbarRef.current
      || !rect
    ) return;

    if (horizontalToolbar) {
      openToolbar(
        horizontalToolbarRef,
        {
          top: rect.top - HORIZONTAL_TOOLBAR_HEIGHT,
          left: calculateLeftPosition(rect, HORIZONTAL_TOOLBAR_WIDTH),
        },
      );
    }

    if (verticalToolbar) {
      openToolbar(verticalToolbarRef, {
        top: calculateTopPosition(rect, VERTICAL_TOOLBAR_HEIGHT),
        left: calculateLeftPosition(rect, VERTICAL_TOOLBAR_WIDTH),
      });
    }

    if (mentionToolbar) {
      openToolbar(mentionToolbarRef, {
        top: calculateMentionToolbarTop(rect, MENTION_TOOLBAR_HEIGHT),
        left: calculateLeftPosition(rect, MENTION_TOOLBAR_WIDTH),
      });
    }
  }, [
    usingToolbar,
    horizontalToolbar,
    verticalToolbar,
    mentionToolbar,
    editor.selection,
  ]);

  useEffect(() => {
    if (isSafariBrowser()) return;
    if (
      !horizontalToolbarRef.current
      || !verticalToolbarRef.current
      || !mentionToolbarRef.current
    ) return;

    if (!horizontalToolbar) {
      closeToolbar(horizontalToolbarRef);
    }

    if (!verticalToolbar && outsideVerticalToolbar) {
      closeToolbar(verticalToolbarRef);
    }

    if (!mentionToolbar && outsideMentionToolbar) {
      closeToolbar(mentionToolbarRef);
    }
  }, [
    horizontalToolbar,
    verticalToolbar,
    mentionToolbar,
    outsideVerticalToolbar,
    outsideMentionToolbar,
  ]);

  useEffect(() => {
    if (editorSelection) setLastEditorSelection(editorSelection);
  }, [editorSelection]);

  return (
    <>
      <HorizontalToolbar
        toolbarRef={horizontalToolbarRef}
        permissionUsingTableField={permissionUsingTableField}
        permissionUsingAttachmentField={permissionUsingAttachments}
        closeToolbar={closeToolbar}
      />
      <VerticalToolbar
        toolbarRef={verticalToolbarRef}
        toolbarInputRef={verticalToolbarInputRef}
        lastEditorSelection={lastEditorSelection}
      />
      <MentionToolbar
        toolbarRef={mentionToolbarRef}
        toolbarInputRef={mentionToolbarInputRef}
        lastEditorSelection={lastEditorSelection}
      />
    </>
  );
};

export default Toolbar;